import type { SegmentsOptions } from '@openx/types/targeting/customVariables.ts';
import { useMemo } from 'react';

import { useTargetingContext } from '../../../utils/context/TargetingContext.tsx';

export type UseGetContextualData = (accountUid: string) => {
  loading: boolean;
  providerId?: string;
  providerName?: string;
  segments: SegmentsOptions;
};

/** The contextual provider connected to the account, without fetching its segments. */
export const useAccountContextualProvider = (accountUid: string) => {
  const { useRelatedAccountsFetch } = useTargetingContext();
  const { relatedAccounts } = useRelatedAccountsFetch([]);

  const accountUidFromProps = accountUid || '';
  const providerId = useMemo(() => {
    return Object.keys(relatedAccounts?.[accountUidFromProps]?.contextual_providers || [])[0];
  }, [accountUidFromProps, relatedAccounts]);

  return { accountId: relatedAccounts?.[accountUidFromProps]?.id, providerId };
};

export const useGetContextualData: UseGetContextualData = (accountUid: string) => {
  const { useFetchSegmentsV2 } = useTargetingContext();
  const { accountId, providerId: contextualProvider } = useAccountContextualProvider(accountUid);

  const { segments, loading } = useFetchSegmentsV2({
    account_id: accountId,
    provider_id: contextualProvider,
    limit: 9000,
    offset: 0,
  });

  return {
    loading,
    providerId: contextualProvider,
    providerName: segments?.[0]?.provider_name,
    segments,
  };
};
